import React from 'react';
import { motion } from 'framer-motion';

const AppDownload: React.FC = () => {
  
  // Mağaza butonları için pasif tıklama engelleyici (sadece App Store için)
  const handleStoreClick = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  return (
    <section id="download" className="relative py-24 overflow-hidden bg-primary-900">
      
      {/* Arka Plan Efektleri */}
      <div className="absolute inset-0 z-0">
        {/* Dairesel Işık Hüzmeleri */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-secondary-500/20 rounded-full blur-[128px] mix-blend-screen animate-pulse-slow"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary-500/20 rounded-full blur-[128px] mix-blend-screen"></div>
        
        {/* Tekno Desen (Grid) */}
        <div className="absolute inset-0 opacity-[0.03]" 
             style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
        </div>
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          
          {/* Glassmorphism Kartı */}
          <motion.div 
            className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-16 text-center overflow-hidden"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Dekoratif Üst Işık */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-1 bg-gradient-to-r from-transparent via-secondary-400 to-transparent opacity-50"></div>

            {/* Logo Alanı */}
            <motion.div 
              className="relative w-32 h-32 mx-auto mb-8"
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {/* Logo Arkası Glow */}
              <div className="absolute inset-0 bg-secondary-500/30 rounded-full blur-2xl animate-pulse"></div>
              <img 
                src="/exereyes_logo_whitebr.png" 
                alt="ExerEyes Logo" 
                className="relative w-full h-full object-contain drop-shadow-2xl"
              />
            </motion.div>

            {/* Başlık ve Açıklama */}
            <motion.h2
              className="text-3xl md:text-5xl font-display font-bold text-white mb-6 tracking-tight"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              Exereyes <span className="text-secondary-400">Google Play Store'da</span> Yayında
            </motion.h2>

            <motion.p
              className="text-lg md:text-xl text-primary-100 mb-12 max-w-2xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              %100 yerli mühendislik ile geliştirilen yapay zeka destekli kişisel antrenörünüz ile tanışın. Evinizdeki sıradan nesneleri 
              egzersiz ekipmanına dönüştüren devrimsel teknoloji artık cebinizde.
            </motion.p>

            {/* Mağaza Butonları (Large Versions) */}
            <motion.div 
              className="flex flex-col sm:flex-row items-center justify-center gap-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              {/* App Store Button */}
              <StoreButton 
                href="#" 
                onClick={handleStoreClick}
                icon={
                  <svg viewBox="0 0 384 512" fill="currentColor" className="w-8 h-8">
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 52.3-11.4 69.5-34.3z"/>
                  </svg>
                }
                label="Download on the"
                storeName="App Store"
              />

              {/* Google Play Button */}
              <StoreButton 
                href="https://play.google.com/store/apps/details?id=com.exereyes.iuc" 
                target="_blank"
                rel="noopener noreferrer"
                icon={
                  <svg viewBox="0 0 512 512" fill="currentColor" className="w-8 h-8">
                    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/>
                  </svg>
                }
                label="GET IT ON"
                storeName="Google Play"
              />
            </motion.div>
            
            {/* Durum Etiketi */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="mt-8"
            >
              <span className="inline-flex items-center px-4 py-1 rounded-full bg-secondary-500/10 border border-secondary-500/20 text-secondary-300 text-sm font-medium">
                <span className="w-2 h-2 bg-secondary-500 rounded-full mr-2"></span>
                Kullanıcıyla Buluşmuş Teknoloji
              </span>
            </motion.div>

          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Özel Store Buton Bileşeni
interface StoreButtonProps {
  href: string;
  onClick?: (e: React.MouseEvent) => void;
  icon: React.ReactNode;
  label: string;
  storeName: string;
  target?: string;
  rel?: string;
}

const StoreButton: React.FC<StoreButtonProps> = ({ href, onClick, icon, label, storeName, target, rel }) => (
  <a 
    href={href}
    onClick={onClick}
    target={target}
    rel={rel}
    className="group flex items-center bg-black/40 hover:bg-black/60 border border-white/20 hover:border-white/40 backdrop-blur-sm text-white rounded-xl px-6 py-3 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl min-w-[200px]"
  >
    <div className="mr-4 text-white/90 group-hover:text-white transition-colors">
      {icon}
    </div>
    <div className="flex flex-col items-start">
      <span className="text-[10px] uppercase tracking-wider opacity-70">{label}</span>
      <span className="text-lg font-bold leading-none mt-1">{storeName}</span>
    </div>
  </a>
);

export default AppDownload;